<template>
  <div>
    <RfSimpleCard class="mb-5">
      <div class="px-7.5 py-5.5">
        <p class="mb-0">
          You can import additional user information here. From your data warehouse, you may create
          a one time or recurring export in comma delimited format of any additional information
          that may be useful for targeting Guides or Prompts. For example, the user's lifetime value
          may be used to determine the amount of a save offer. Refer to the
          <a target="_blank" href="https://docs.recurly.com/recurly-engage/docs/sync-user-traits"
            >documentation</a
          >
          for additional information.
        </p>
      </div>
    </RfSimpleCard>
    <RfSimpleCard class="rf-retention-form">
      <v-dialog v-model="showEditDialog" persistent maxWidth="700">
        <v-card tag="form" @submit.prevent="onSubmit">
          <v-card-title class="headline">Update trait</v-card-title>
          <v-card-text>
            <div class="input-wrapper">
              <RfTextInput
                v-model="custom_field.label"
                variant="outlined"
                density="compact"
                title="Label"
                placeholder="Label"
                :disabled="
                  ['aws_s3', 'rsm'].includes(custom_field.connector_type) || userStore.isReadOnly
                "
              />
              <RfTextInput
                v-model="custom_field.description"
                variant="outlined"
                density="compact"
                title="Description"
                placeholder="Description"
                :disabled="userStore.isReadOnly"
              />
              <RfSelect
                v-model="custom_field.data_type"
                variant="outlined"
                density="compact"
                :items="Object.keys(dataTypes)"
                title="Type"
                placeholder="Type"
                :disabled="typesDisabled"
                returnObject
              />
              <RfSelect
                id="trait-value"
                v-model="custom_field.display_type"
                variant="outlined"
                density="compact"
                :items="Object.keys(displayTypes)"
                title="Display"
                placeholder="Display"
                :disabled="typesDisabled"
                name="trait_value"
                no-data-text="Select a data type first"
              />
            </div>
            <v-checkbox
              v-if="canNormalize"
              v-model="custom_field.is_normalized"
              label="Normalize data into 0-10 scale"
              style="margin-top: 0"
              :disabled="typesDisabled"
            ></v-checkbox>
            <div
              v-if="
                (userStore.isCurrentSuperAdmin || userStore.isCurrentAdmin) &&
                custom_field.values &&
                custom_field.values.length > 0
              "
              class="mt-4"
            >
              <div class="mb-2 flex items-center justify-between">
                <h6 class="mb-0">Values:</h6>
                <v-btn
                  size="small"
                  variant="outlined"
                  class="text-capitalize"
                  @click="showValues = !showValues"
                >
                  {{ showValues ? "Hide" : "Show" }} Values ({{ custom_field.values.length }})
                </v-btn>
              </div>
              <div v-if="showValues" class="values-display">
                <div class="mb-3" style="display: flex; gap: 8px; width: 100%">
                  <RfTextInput
                    v-model="newValue"
                    variant="outlined"
                    density="compact"
                    placeholder="Add new value"
                    hideDetails
                    size="24"
                    style="flex: 1; min-width: 0; width: 75%"
                    @keyup.enter="addValue"
                  />
                  <v-btn
                    size="small"
                    variant="outlined"
                    color="primary"
                    :disabled="!newValue.trim()"
                    style="flex-shrink: 0"
                    @click="addValue"
                  >
                    Add
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="outlined"
                    :disabled="!custom_field.values || custom_field.values.length < 2"
                    style="flex-shrink: 0"
                    @click="sortValues"
                  >
                    Sort Alphabetically
                  </v-btn>
                </div>
                <div class="values-chips">
                  <v-chip
                    v-for="(value, index) in custom_field.values"
                    :key="value"
                    size="small"
                    class="mr-2 mb-2"
                    variant="outlined"
                    closable
                    @click:close="removeValue(index)"
                  >
                    {{ value }}
                  </v-chip>
                </div>
              </div>
            </div>
            <div v-if="typesDisabled">
              <v-icon size="16" start color="warning">md:warning</v-icon>Contact support to change
              these values.
            </div>
          </v-card-text>
          <v-card-actions class="modal-card-actions-div">
            <v-btn
              class="cancel-btn"
              variant="outlined"
              style="width: 100px"
              @click="showEditDialog = false"
              >Cancel</v-btn
            >
            <RfButton
              icon="md:update"
              buttonText="Update"
              color="success"
              type="submit"
              class="px-7!"
              :disabled="userStore.isDisabledWithMessage"
            />
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog :modelValue="!!affectedSegmentsAfterTypeChange.length" maxWidth="600">
        <v-card>
          <v-card-title class="headline">Review Affected Segments</v-card-title>
          <v-card-text>
            <p class="mb-4">
              The updated trait is used in the below segments. Check if the type change broke
              segment filters:
            </p>
            <ul class="list-disc pl-5">
              <li v-for="segment in affectedSegmentsAfterTypeChange" :key="segment.id" class="mb-2">
                <a :href="`/apps/${app.id}/segments/${segment.id}`" target="_blank">
                  {{ segment.name }}
                </a>
              </li>
            </ul>
          </v-card-text>
          <v-card-actions class="modal-card-actions-div">
            <v-btn class="cancel-btn" variant="outlined" @click="resetForm"> Close </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <RfAwsCredentialsModal
        ref="awsCredentialsModal"
        :loading="appsStore.currAppAwsCredentials.loading"
        :awsUserName="app.aws_creds?.user_name"
        :awsPath="app.aws_creds?.aws_path"
        :awsAccessKey="
          appsStore.currAppAwsCredentials.value?.access_key_id || app?.aws_creds?.access_key_id
        "
        :awsSecretKey="
          appsStore.currAppAwsCredentials.value?.secret_access_key ||
          app?.aws_creds?.secret_access_key
        "
        @update:showCredentials="
          value =>
            value
              ? appsStore.getAppAwsCredentials(app.id)
              : (appsStore.currAppAwsCredentials.value = {})
        "
      />
      <div class="px-7.5 py-5">
        <div class="rf-settings-section-title flex items-center justify-between">
          <div class="flex flex-col">
            <h4 class="mb-3">Upload to your Recurly Engage AWS S3 area</h4>
            <span>
              <RfButton
                buttonText="Click here for AWS S3 credentials"
                link
                :disabled="userStore.isDisabledWithMessage"
                @click="awsCredentialsModal?.show()"
              />
            </span>
          </div>
          <RfButtonNew
            type="secondary"
            size="x-small"
            :disabled="userStore.isDisabledWithMessage"
            @click="$refs.file.click()"
          >
            <RfAddIcon class="h-5! w-5!" />
            <input ref="file" class="hidden" type="file" name="upload_csv" @change="onUploadCSV" />
            Upload CSV
          </RfButtonNew>
        </div>
        <RfAlert type="warning" class="mb-7">
          Once the CSV has been uploaded, it may take up to 30 minutes before traits are available
          here.
        </RfAlert>
        <v-expansion-panels v-model="customTraitsPanel" multiple>
          <v-expansion-panel v-for="(fields, key) in fields_by_visible_connector_type" :key="key">
            <v-expansion-panel-title>{{ getConnectorName(fields.label) }}</v-expansion-panel-title>
            <v-expansion-panel-text class="*:px-0!">
              <v-table class="rf-settings-table-data mt-5">
                <template #default>
                  <thead>
                    <tr>
                      <th class="text-left">Trait</th>
                      <th class="text-left">Type</th>
                      <th class="text-left">Display</th>
                      <th v-if="fields.label === 'redfast'" class="text-left">Value</th>
                      <th v-if="userStore.isCurrentSuperAdmin" class="text-left">Redacted</th>
                      <th class="text-left"></th>
                    </tr>
                  </thead>
                  <tbody v-if="filteredValues(fields) && filteredValues(fields).length > 0">
                    <tr v-for="(custom, index) in filteredValues(fields)" :key="index">
                      <td>{{ custom.label }}</td>
                      <td>{{ custom.data_type }}</td>
                      <td>{{ custom.display_type }}</td>
                      <td v-if="fields.label === 'redfast'">
                        {{ custom.values ? custom.values[0] : "" }}
                      </td>
                      <td v-if="userStore.isCurrentSuperAdmin">
                        <v-checkbox
                          :modelValue="isTraitRedacted(custom.label)"
                          hideDetails
                          density="compact"
                          @update:modelValue="toggleRedactedTrait(custom.label)"
                        />
                      </td>
                      <td>
                        <RfButton
                          v-if="!['minutes', 'visits'].includes(custom.label)"
                          icon="md:edit"
                          variant="text"
                          color="accent"
                          @click="onEdit(custom)"
                        />
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td :colspan="getColspan(fields)">
                        <div class="settings-no-data-message">Nothing to show.</div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </RfSimpleCard>
  </div>
</template>

<script>
import RfSimpleCard from "@/components/RfSimpleCard.vue";
import RfTextInput from "@/components/legacy/RfInputLegacy.vue";
import RfSelect from "@/components/legacy/RfSelectLegacy.vue";
import RfButton from "@/components/buttons/RfButton.vue";
import { useToastsStore } from "@/pinia/toastsStore";
import RfAwsCredentialsModal from "@/blocks/RfModals/RfAwsCredentialsModal.vue";
import { ref } from "vue";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import { useCustomFieldsStore } from "@/pinia/customFieldsStore";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfAddIcon from "@/components/icons/RfAddIcon.vue";
import { IgnoredErrorMessages } from "@/utils/constants/ErrorConstants";
import RfAlert from "@/components/alerts/RfAlert.vue";

export default {
  name: "RfSetupCustomTraits",
  components: {
    RfSimpleCard,
    RfTextInput,
    RfSelect,
    RfButton,
    RfAwsCredentialsModal,
    RfButtonNew,
    RfAddIcon,
    RfAlert,
  },
  props: { app: { type: Object, default: () => ({}) } },
  setup: () => ({
    toastsStore: useToastsStore(),
    appsStore: useAppsStore(),
    awsCredentialsModal: ref(),
    userStore: useUserStore(),
    customFieldsStore: useCustomFieldsStore(),
  }),
  data() {
    return {
      custom_field: {
        label: "",
        description: "",
        data_type: "",
        display_type: "",
        is_normalized: false,
        name: "",
        is_usage: false,
        values: [],
      },
      showCredentials: false,
      showEditDialog: false,
      showValues: false,
      newValue: "",
      customTraitsPanel: [],
      typesDisabled: false,
      fieldType: "custom",
      affectedSegmentsAfterTypeChange: [],
    };
  },
  computed: {
    redactedTraits() {
      return this.app?.flags?.redacted_user_traits || [];
    },
    custom_fields() {
      return this.customFieldsStore.segmentCustomFields || [];
    },
    hasAllData() {
      return (
        this.custom_field.label.length &&
        this.custom_field.display_type.length &&
        this.custom_field.data_type.length
      );
    },
    fields_by_connector_type() {
      const values = this.custom_fields.reduce((total, item) => {
        const type = item.connector_type;
        if (!total[type]) total[type] = [];
        total[type].push(item);
        return total;
      }, {});

      return ["redfast", "segment", "aws_s3", "rsm", "redfast_system"].map(item => {
        return { label: item, values: values[item] || [] };
      });
    },
    fields_by_visible_connector_type() {
      return this.fields_by_connector_type.filter(item => ["aws_s3", "rsm"].includes(item.label));
    },
    editableFieldIds() {
      // Map of incomplete fields (no display_type/data_type set yet) by connector type
      const connectorTypes = {};
      this.fields_by_connector_type.forEach(typeFields => {
        typeFields.values.forEach(field => {
          if (!field.display_type && !field.data_type) {
            if (connectorTypes[typeFields.label]) {
              connectorTypes[typeFields.label].push(field.id);
            } else {
              connectorTypes[typeFields.label] = [field.id];
            }
          }
        });
      });
      return connectorTypes;
    },
    dataTypes() {
      return { boolean: "Boolean", string: "String", number: "Number", date: "Date" };
    },
    displayTypes() {
      const { data_type } = this.custom_field;
      if (data_type === "boolean") {
        return { multi_select: "Multi Select" };
      }
      if (data_type === "number") {
        return {
          slider: "Slider",
          range_select: "Range Select",
          multi_select: "Multi Select",
          text_input: "Text",
        };
      }
      if (data_type === "string") {
        return { multi_select: "Multi Select", text_input: "Text" };
      }
      if (data_type === "date") {
        return { date_range: "Date Range" };
      }
      return {};
    },
    canNormalize() {
      if (this.custom_field.display_type === "slider") {
        return true;
      }
      return false;
    },
  },
  async mounted() {
    if (!this.custom_fields.length) {
      await this.customFieldsStore.getAppCustomFields({
        appId: this.app.id,
        fieldType: this.fieldType,
      });
    }

    // Only expand panel if exactly one section has data
    const sectionsWithData = this.fields_by_visible_connector_type
      .map((section, index) => ({
        index,
        hasData: this.filteredValues(section)?.length > 0,
      }))
      .filter(section => section.hasData);

    if (sectionsWithData.length === 1) {
      this.customTraitsPanel = [sectionsWithData[0].index];
    }
  },
  methods: {
    filteredValues(fields) {
      const { label, values } = fields;
      if (label === "aws_s3") {
        return values.filter(
          item =>
            !item.data_type || ["boolean", "string", "number", "date"].indexOf(item.data_type) > -1,
        );
      }
      return values;
    },
    getConnectorName(key) {
      const items = {
        aws_s3: "AWS S3",
        redfast: "Recurly Engage",
        segment: "Segment",
        rsm: "Recurly Subscriptions",
      };

      return items[key] || key;
    },
    resetForm() {
      this.custom_field.label = "";
      this.custom_field.description = "";
      this.custom_field.data_type = "";
      this.custom_field.display_type = "";
      this.custom_field.is_normalized = false;
      this.custom_field.is_usage = false;
      this.custom_field.name = "";
      this.custom_field.values = [];
      this.showValues = false;
      this.newValue = "";
      this.affectedSegmentsAfterTypeChange = [];
    },
    addValue() {
      const newValueTrimmed = this.newValue.trim();
      if (newValueTrimmed && !this.custom_field.values.includes(newValueTrimmed)) {
        this.custom_field.values.push(newValueTrimmed);
        this.newValue = "";
      }
    },
    removeValue(index) {
      this.custom_field.values.splice(index, 1);
    },
    sortValues() {
      if (this.custom_field.values && this.custom_field.values.length > 1) {
        this.custom_field.values.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      }
    },
    onUploadCSV() {
      fetch(this.app.uploadable_csv_path, {
        method: "PUT",
        headers: { "Content-Type": "text/csv" },
        body: this.$refs.file.files[0],
      })
        .then(() =>
          this.toastsStore.create({ type: "success", body: "CSV was uploaded successfully" }),
        )
        .catch(() =>
          this.toastsStore.create({ type: "error", body: "CSV upload failed. Please try again" }),
        );
    },
    async onSubmit() {
      if (this.custom_field.id) {
        try {
          const json = await this.customFieldsStore.updateAppCustomField({
            appId: this.app.id,
            fieldId: this.custom_field.id,
            model: this.custom_field,
            fieldType: this.fieldType,
          });

          if (!json) throw new Error("Failed to update trait");

          this.toastsStore.create({ type: "success", body: "Trait updated successfully" });
          this.resetForm();

          if (json.affected_segments?.length) {
            this.affectedSegmentsAfterTypeChange = json.affected_segments;
          }
        } catch (e) {
          if (!IgnoredErrorMessages.includes(e.message)) {
            this.toastsStore.create({
              type: "error",
              body: `Failed to update trait: ${e.message}`,
            });
          }
          return;
        }
      }
      this.showEditDialog = false;
    },
    onEdit(custom) {
      this.showValues = false;
      this.newValue = "";
      this.showEditDialog = true;
      custom.values = custom.values || [];
      this.custom_field = { ...custom };
      this.typesDisabled =
        this.isEditFieldDisabled(this.custom_field) || !!this.userStore.isDisabledWithMessage;
    },
    isEditFieldDisabled(field) {
      // Superadmins can edit trait types
      if (this.userStore.isCurrentSuperAdmin && field.connector_type === "aws_s3") return false;
      // Allow editing incomplete fields otherwise
      return !this.editableFieldIds[field.connector_type]?.includes(field.id);
    },
    isTraitRedacted(label) {
      return this.redactedTraits.includes(label.toLowerCase());
    },
    async toggleRedactedTrait(label) {
      if (!this.app) return;

      const redactedTraits = [...this.redactedTraits];
      const lowerLabel = label.toLowerCase();
      const index = redactedTraits.indexOf(lowerLabel);

      if (index > -1) {
        redactedTraits.splice(index, 1);
      } else {
        redactedTraits.push(lowerLabel);
      }

      // Update app flags
      const updatedApp = {
        ...this.app,
        flags: {
          ...(this.app.flags || {}),
          redacted_user_traits: redactedTraits,
        },
      };

      try {
        await this.appsStore.updateApp({ appObj: updatedApp });
        this.toastsStore.create({
          type: "success",
          body: `Trait "${label}" ${index > -1 ? "removed from" : "added to"} redacted list`,
        });
      } catch (_e) {
        this.toastsStore.create({
          type: "error",
          body: "Failed to update redacted traits. Please try again.",
        });
      }
    },
    getColspan(fields) {
      // Base columns: Trait, Type, Display, Edit (4)
      let colspan = 4;
      // Add 1 for Value column if this is redfast
      if (fields.label === "redfast") colspan++;
      // Add 1 for Redacted column if user is super admin
      if (this.userStore.isCurrentSuperAdmin) colspan++;
      return colspan;
    },
  },
};
</script>

<style lang="scss" scoped>
.input-wrapper {
  gap: 0px 20px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(max(300px, 45%), 1fr));
  grid-template-rows: min-content;
}
</style>
